import { ArrayUtils, engineComponent, StringUtils } from "neo-common";
import { Style } from "./Style";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { StyleManager } from "../../Managers/StyleManager";
import { ParticleElementAttributes } from "../Graphics/ParticleElement";

export interface IZoneOptions {
    type: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    innerWidth?: number;
    innerHeight?: number;
    radius?: number;
    minRadius?: number;
    radiusB?: number;
    minRadiusB?: number;
    innerRadius?: number;
    minInnerRadius?: number;
    innerRadiusB?: number;
    minInnerRadiusB?: number;
    innerX?: number;
    innerY?: number;
    angle?: number;
    startAngle?: number;
    //    Fountain
    startX?: Array<number>;
    startY?: Array<number>;
    intervalX?: Array<number>;
    intervalY?: Array<number>;
    bezierY?: Array<number>;
    bezierEllipse?: boolean;
}

export interface IPathOptions {
    x?: number;
    y?: number;
    randomX?: number;
    randomY?: number;
}

@engineComponent(ComponentTypes.Style)
// TODO add comments
export class ParticleStyle extends Style {
    // value in percentage for creating reserv in display list to pooling particles
    static ElementsReserve: number = 0.5;

    // names for zones
    static ZoneRectangle: string = "Rectangle";
    static ZoneLine: string = "Line";
    static ZoneEllipse: string = "Ellipse";
    static ZoneTarget: string = "Target";
    static ZoneDynamic: string = "Dynamic";
    static ZoneFountain: string = "Fountain";

    static NodeCount: string = "Count";
    static NodeLife: string = "Life";
    static NodeImages: string = "Images";
    static NodePosition: string = "Position";
    static NodePath: string = "Path";
    static NodeScale: string = "Scale";
    static NodeRotation: string = "Rotation";
    static NodeAlpha: string = "Alpha";
    static NodeValue: string = "Value";

    // TODO possible memory leak
    node: Element;

    private _count: number;
    private _time: number;
    private _minLife: number;
    private _maxLife: number;
    private _images: Array<string>;
    private _randomStartFrame: boolean;
    private _subParticleStyle: string;
    private _positionZone: IZoneOptions;
    private _pathValues: Array<IPathOptions>;
    private _scaleValues: Array<Array<number>>;
    private _rotationValues: Array<Array<number>>;
    private _alphaValues: Array<Array<number>>;

    constructor() {
        super();

        this._count = 1;
        this._time = 60;
        this._minLife = 60;
        this._maxLife = 120;
        this._images = [];
        this._randomStartFrame = false;
        this._subParticleStyle = "";
        this._positionZone = {
            type: ParticleStyle.ZoneRectangle,
            x: 0,
            y: 0,
            innerX: 0,
            innerY: 0,
            width: StageController.Width,
            height: StageController.Height,
            innerWidth: -1,
            innerHeight: -1,
            radius: 0,
            minRadius: 0,
            radiusB: 0,
            minRadiusB: 0,
            innerRadius: 0,
            minInnerRadius: 0,
            innerRadiusB: 0,
            minInnerRadiusB: 0,
            angle: 360,
            startAngle: 0, //used to prevent container rotation deformation with ellipse,
            startX: [960],
            startY: [540],
            intervalX: [170, 670],
            intervalY: [200, 1000],
            bezierY: [300, 900],
            bezierEllipse: false,
        };
        this._pathValues = [];
        this._scaleValues = [];
        this._rotationValues = [];
        this._alphaValues = [];
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element) {
        this.node = node;

        super.create(node);

        if (node.hasAttribute(ParticleElementAttributes.EXTEND_STYLE)) {
            let extendStyle: ParticleStyle = <ParticleStyle>(
                StyleManager.Styles[node.getAttribute(ParticleElementAttributes.EXTEND_STYLE)]
            );
            if (extendStyle != null && extendStyle.node != null) {
                this.parseChildrenNodes(extendStyle.node);
            }
        }

        this.parseChildrenNodes(node);
    }

    private parseChildrenNodes(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case ParticleStyle.NodeCount:
                    this.initializeCount(next);
                    break;
                case ParticleStyle.NodeLife:
                    this.initializeLife(next);
                    break;
                case ParticleStyle.NodeImages:
                    this.initializeImages(next);
                    break;
                case ParticleStyle.NodePosition:
                    this.initializePosition(next);
                    break;
                case ParticleStyle.NodePath:
                    this.initializePath(next);
                    break;
                case ParticleStyle.NodeScale:
                    this.initializeScale(next);
                    break;
                case ParticleStyle.NodeRotation:
                    this.initializeRotation(next);
                    break;
                case ParticleStyle.NodeAlpha:
                    this.initializeAlpha(next);
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown node in particle style " + next.nodeName);
                    }
            }
            next = next.nextElementSibling;
        }
    }

    get count(): number {
        return this._count;
    }

    get time(): number {
        return this._time;
    }

    get minLife(): number {
        return this._minLife;
    }

    get maxLife(): number {
        return this._maxLife;
    }

    get images(): Array<string> {
        return this._images;
    }

    get randomStartFrame(): boolean {
        return this._randomStartFrame;
    }

    get subParticleStyle(): string {
        return this._subParticleStyle;
    }

    get positionZone(): IZoneOptions {
        return this._positionZone;
    }

    get pathValues(): Array<IPathOptions> {
        return this._pathValues;
    }

    get scaleValues(): Array<Array<number>> {
        return this._scaleValues;
    }

    get rotationValues(): Array<Array<number>> {
        return this._rotationValues;
    }

    get alphaValues(): Array<Array<number>> {
        return this._alphaValues;
    }

    calculateElements(): number {
        let elementsPerSecond: number = (StageController.FPS / this._time) * this._count;
        let elementsWithLife: number = (this._maxLife / StageController.FPS) * elementsPerSecond;
        return elementsWithLife;
    }

    /**
     * Initialize elements per second.
     * Count - how much elements
     * Time - time when specified count of elements is generated
     *
     * Example:
     *  <Count Count="8" Time="2"/>
     * @param node
     */
    private initializeCount(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_COUNT)) {
            this._count = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_COUNT));
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_TIME)) {
            this._time = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_TIME));
        }
    }

    /**
     * Example:
     *  <Life Min="500" Max="1000"/>
     * @param node
     */
    private initializeLife(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN)) {
            this._minLife = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_MIN));
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_MAX)) {
            this._maxLife = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_MAX));
        }

        if (this._minLife > this._maxLife) {
            this._maxLife = this._minLife;
        }
    }

    /**
     * Example:
     *  <Images Images="GFX_particle1;GFX_particle2;..."/>
     * @param node
     */
    private initializeImages(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_FROM_FRAME)) {
            this._images = node.getAttribute(ParticleStyleAttributes.ATTR_FROM_FRAME).split(";");

            for (let i: number = 0; i < this._images.length; i++) {
                // TODO 0000
                this._images[i] = this._images[i] + "0000";
            }
        } else if (node.hasAttribute(ParticleStyleAttributes.ATTR_IMAGES)) {
            this._images = node.getAttribute(ParticleStyleAttributes.ATTR_IMAGES).split(";");
        }

        if (node.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_START_FRAME)) {
            this._randomStartFrame = StringUtils.parseBoolean(
                node.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_START_FRAME),
            );
        }

        if (node.hasAttribute(ParticleStyleAttributes.ATTR_SUB_PARTICLE_STYLE)) {
            this._subParticleStyle = node.getAttribute(ParticleStyleAttributes.ATTR_SUB_PARTICLE_STYLE);
        }
    }

    /**
     * Bornplace of elements.
     * Example:
     *  <Position Type="Rectangle" X="0" Y="-100" Width="288" Height="1080"/>
     * @param node
     */
    private initializePosition(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_TYPE)) {
            this._positionZone.type = node.getAttribute(ParticleStyleAttributes.ATTR_TYPE);
            switch (this._positionZone.type) {
                case ParticleStyle.ZoneRectangle:
                    // TODO separate method get Zone
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                        this._positionZone.x = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_X));
                        this._positionZone.innerX = this._positionZone.x;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                        this._positionZone.y = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_Y));
                        this._positionZone.innerY = this._positionZone.y;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_X)) {
                        this._positionZone.innerX = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_X));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_Y)) {
                        this._positionZone.innerY = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_Y));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_WIDTH)) {
                        this._positionZone.width = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_WIDTH));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_HEIGHT)) {
                        this._positionZone.height = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_HEIGHT));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_WIDTH)) {
                        this._positionZone.innerWidth = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_WIDTH),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_HEIGHT)) {
                        this._positionZone.innerHeight = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_HEIGHT),
                        );
                    }
                    break;
                case ParticleStyle.ZoneTarget:
                    this._positionZone.width = 0;
                    this._positionZone.height = 0;

                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_WIDTH)) {
                        this._positionZone.width = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_WIDTH));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_HEIGHT)) {
                        this._positionZone.height = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_HEIGHT));
                    }

                    break;
                case ParticleStyle.ZoneDynamic:
                    break;
                case ParticleStyle.ZoneLine:
                    // TODO
                    break;
                case ParticleStyle.ZoneEllipse:
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                        this._positionZone.x = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_X));
                        this._positionZone.innerX = this._positionZone.x;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                        this._positionZone.y = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_Y));
                        this._positionZone.innerY = this._positionZone.y;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_X)) {
                        this._positionZone.innerX = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_X));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_Y)) {
                        this._positionZone.innerY = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_Y));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_RADIUS)) {
                        let radius = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_RADIUS));
                        //set all attributes by radius, if we have only circle with no minRadius
                        this._positionZone.radius = radius;
                        this._positionZone.radiusB = radius;
                        this._positionZone.minRadius = radius;
                        this._positionZone.minRadiusB = radius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS)) {
                        this._positionZone.minRadius = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS),
                        );
                        this._positionZone.minRadiusB = this._positionZone.minRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_RADIUS_B)) {
                        this._positionZone.radiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_RADIUS_B),
                        );
                        this._positionZone.minRadiusB = this._positionZone.radiusB;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS_B)) {
                        this._positionZone.minRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS_B),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS)) {
                        let innerRadius = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS));
                        this._positionZone.innerRadius = innerRadius;
                        this._positionZone.innerRadiusB = innerRadius;
                        this._positionZone.minInnerRadius = innerRadius;
                        this._positionZone.minInnerRadiusB = innerRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS)) {
                        this._positionZone.minInnerRadius = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS),
                        );
                        this._positionZone.minInnerRadiusB = this._positionZone.minInnerRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS_B)) {
                        this._positionZone.innerRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS_B),
                        );
                        this._positionZone.minInnerRadiusB = this._positionZone.innerRadiusB;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS_B)) {
                        this._positionZone.minInnerRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS_B),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_START_ANGLE)) {
                        let startAngle = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_START_ANGLE));
                        this._positionZone.startAngle = startAngle;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_ANGLE)) {
                        let angle = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_ANGLE));
                        this._positionZone.angle = angle;
                    }
                    if (node.hasAttribute("BezierEllipse")) {
                        this._positionZone.bezierEllipse = StringUtils.parseBoolean(node.getAttribute("BezierEllipse"));
                    }
                    break;

                case ParticleStyle.ZoneFountain:
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                        this._positionZone.x = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_X));
                        this._positionZone.innerX = this._positionZone.x;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                        this._positionZone.y = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_Y));
                        this._positionZone.innerY = this._positionZone.y;
                    }

                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_START_X)) {
                        this._positionZone.startX = ArrayUtils.numberArray(
                            node.getAttribute(ParticleStyleAttributes.ATTR_START_X),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_START_Y)) {
                        this._positionZone.startY = ArrayUtils.numberArray(
                            node.getAttribute(ParticleStyleAttributes.ATTR_START_Y),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INTERVAL_X)) {
                        this._positionZone.intervalX = ArrayUtils.numberArray(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INTERVAL_X),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INTERVAL_Y)) {
                        this._positionZone.intervalY = ArrayUtils.numberArray(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INTERVAL_Y),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_BEZIER_Y)) {
                        this._positionZone.bezierY = ArrayUtils.numberArray(
                            node.getAttribute(ParticleStyleAttributes.ATTR_BEZIER_Y),
                        );
                    }

                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown zone " + this._positionZone.type);
                    }
            }
        }
    }

    /**
     * Initialize movement from defined start points to end points with possible random range.
     * Example:
     *  <Path>
     *      <Value X="0" Y="0"/>
     *      <Value X="100" Y="-100" RandomX="100" RandomY="-100"/>
     *  </Path>
     * @param node
     */
    private initializePath(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                let path: IPathOptions = {};
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                    path.x = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_X));
                } else {
                    path.x = 0;
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                    path.y = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_Y));
                } else {
                    path.y = 0;
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_X)) {
                    path.randomX = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_X));
                } else {
                    path.randomX = null;
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y)) {
                    path.randomY = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y));
                } else {
                    path.randomY = null;
                }
                this._pathValues.push(path);
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Scale of object.
     * Example:
     *  <Scale>
     *      <Value Values="0.5;1"/>
     *      <Value Values="0.1;0.2"/>
     *  </Scale>
     * @param node
     */
    private initializeScale(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUES)) {
                    this._scaleValues.push(
                        ArrayUtils.numberArray(next.getAttribute(ParticleStyleAttributes.ATTR_VALUES)),
                    );
                }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Rotations of object in degrees.
     * Example:
     *  <Rotation>
     *      <Value Value="0.1"/>
     *      <Value Value="3"/>
     *  </Rotation>
     * @param node
     */
    private initializeRotation(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE)) {
                    this._rotationValues.push(
                        ArrayUtils.numberArray(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE)),
                    );
                }
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Example:
     *  <Alpha>
     *      <Value Value="0.8"/>
     *      <Value Value="1"/>
     *  </Alpha>
     * @param node The Alpha node
     */
    private initializeAlpha(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE)) {
                    this._alphaValues.push(
                        ArrayUtils.numberArray(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE)),
                    );
                }
            }
            next = next.nextElementSibling;
        }
    }
}

const ParticleStyleAttributes = {
    ATTR_COUNT: "Count",
    ATTR_TIME: "Time",
    ATTR_MIN: "Min",
    ATTR_MAX: "Max",
    ATTR_FROM_FRAME: "FromFrame",
    ATTR_IMAGES: "Images",
    ATTR_TYPE: "Type",
    ATTR_X: "X",
    ATTR_Y: "Y",
    ATTR_INNER_X: "InnerX",
    ATTR_INNER_Y: "InnerY",
    ATTR_RADIUS: "Radius",
    ATTR_MIN_RADIUS: "MinRadius",
    ATTR_RADIUS_B: "RadiusB",
    ATTR_MIN_RADIUS_B: "MinRadiusB",
    ATTR_INNER_RADIUS: "InnerRadius",
    ATTR_MIN_INNER_RADIUS: "MinInnerRadius",
    ATTR_INNER_RADIUS_B: "InnerRadiusB",
    ATTR_MIN_INNER_RADIUS_B: "MinInnerRadiusB",
    ATTR_ANGLE: "Angle",
    ATTR_START_ANGLE: "StartAngle",
    ATTR_RANDOM_X: "RandomX",
    ATTR_RANDOM_Y: "RandomY",
    ATTR_WIDTH: "Width",
    ATTR_HEIGHT: "Height",
    ATTR_INNER_WIDTH: "InnerWidth",
    ATTR_INNER_HEIGHT: "InnerHeight",
    ATTR_VALUES: "Values",
    ATTR_VALUE: "Value",
    ATTR_RANDOM_START_FRAME: "RandomStartFrame",
    ATTR_SUB_PARTICLE_STYLE: "SubParticleStyle",

    // FOUNTAIN
    ATTR_START_X: "StartX",
    ATTR_START_Y: "StartY",
    ATTR_INTERVAL_X: "IntervalX",
    ATTR_INTERVAL_Y: "IntervalY",
    ATTR_BEZIER_Y: "BezierY",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Styles/ParticleStyle.ts
 **/